import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiSendForm, ApiError } from '../api/client';
import { todayIso } from '../lib/format';

/** Add/edit a revenue row. Ports frm{Business}FitimetPages. */

interface Article {
  id: number;
  artikulli: string;
}

interface Detail {
  ID: number;
  data: string;
  artikulliName: string | null;
  cmimi: number;
  numri: number | null;
  periudha: string | null;
  file_name: string | null;
  hasFile: number;
}

export default function FitimModal({
  biznesi,
  businessName,
  rowId,
  onClose,
  onSaved,
}: {
  biznesi: string;
  businessName: string;
  rowId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = rowId !== null;

  /**
   * Starbar has no unit count. frmStarbarFitimetPages has no txtNumri control
   * and hardcodes `numri = DBNull.Value` on save (line 235), unlike the Stargym
   * and Missfit dialogs. Bar sales are money, not memberships-sold.
   */
  const showNumri = biznesi !== 'starbar';

  const [data, setData] = useState(todayIso);
  const [artikulli, setArtikulli] = useState('');
  const [cmimi, setCmimi] = useState('');
  const [numri, setNumri] = useState('');
  const [periudha, setPeriudha] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const optionsQuery = useQuery({
    queryKey: ['fitimet-articles', biznesi],
    queryFn: () =>
      apiGet<{ articles: Article[]; periudhaOptions: string[] }>(`/fitimet/${biznesi}/artikujt`),
  });

  const detailQuery = useQuery({
    queryKey: ['fitim', rowId],
    queryFn: () => apiGet<Detail>(`/fitimet/rreshti/${rowId}`),
    enabled: isEdit,
  });

  useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    setData(d.data.slice(0, 10));
    setArtikulli(d.artikulliName ?? '');
    setCmimi(String(d.cmimi ?? ''));
    setNumri(d.numri == null ? '' : String(d.numri));
    setPeriudha(d.periudha ?? '');
    setExistingFileName(Number(d.hasFile) === 1 ? (d.file_name ?? 'fatura') : null);
  }, [detailQuery.data]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!artikulli.trim()) {
      setError('Ju lutem zgjidhni një artikull.');
      return;
    }
    const price = Number(cmimi);
    if (cmimi.trim() === '' || !Number.isFinite(price)) {
      setError('Ju lutem vendosni një çmim të vlefshëm.');
      return;
    }
    if (showNumri && numri.trim() !== '' && !Number.isFinite(Number(numri))) {
      setError('Ju lutem vendosni një numër të vlefshëm.');
      return;
    }

    const form = new FormData();
    form.set('data', data);
    form.set('artikulli', artikulli.trim());
    form.set('cmimi', String(price));
    if (showNumri && numri.trim() !== '') form.set('numri', String(Number(numri)));
    if (periudha) form.set('periudha', periudha);
    if (removeFile) form.set('removeFile', 'true');
    if (file) form.set('file', file);

    setSaving(true);
    try {
      if (isEdit) {
        await apiSendForm(`/fitimet/${biznesi}/rreshtat/${rowId}`, form, 'PUT');
      } else {
        await apiSendForm(`/fitimet/${biznesi}/rreshtat`, form, 'POST');
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ruajtja dështoi. Provoni përsëri.');
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? 'Ndrysho fitimin' : 'Shto fitim'}
          </h2>
          <p className="text-sm text-muted">{businessName}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="f-data">
              Data
            </label>
            <input
              id="f-data"
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="f-cmimi">
              Shuma
            </label>
            <input
              id="f-cmimi"
              inputMode="decimal"
              value={cmimi}
              onChange={(e) => setCmimi(e.target.value.replace(/[^\d.]/g, ''))}
              required
              className={`tnum ${inputClass}`}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="f-artikulli">
              Artikulli
            </label>
            {/* Closed list — frmStargymFitimetPages.Designer.cs:181. */}
            <select
              id="f-artikulli"
              value={artikulli}
              onChange={(e) => setArtikulli(e.target.value)}
              required
              className={inputClass}
            >
              <option value="">Zgjidhni një artikull</option>
              {optionsQuery.data?.articles.map((a) => (
                <option key={a.id} value={a.artikulli}>
                  {a.artikulli}
                </option>
              ))}
            </select>
          </div>

          {showNumri && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="f-numri">
                Numri
              </label>
              <input
                id="f-numri"
                inputMode="decimal"
                value={numri}
                onChange={(e) => setNumri(e.target.value.replace(/[^\d.]/g, ''))}
                className={`tnum ${inputClass}`}
              />
              <p className="mt-1 text-xs text-muted">Sa njësi u shitën (përdoret në raporte)</p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="f-periudha">
              Periudha
            </label>
            <select
              id="f-periudha"
              value={periudha}
              onChange={(e) => setPeriudha(e.target.value)}
              className={`${inputClass} bg-white`}
            >
              <option value="">—</option>
              {(optionsQuery.data?.periudhaOptions ?? []).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="f-file">
              Fatura
            </label>
            {existingFileName && !file && !removeFile && (
              <div className="mb-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span className="truncate text-slate-700">{existingFileName}</span>
                <button
                  type="button"
                  onClick={() => setRemoveFile(true)}
                  className="ml-2 shrink-0 text-xs font-medium text-red-600 hover:underline"
                >
                  Hiq
                </button>
              </div>
            )}
            {removeFile && (
              <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Fatura ekzistuese do të hiqet kur të ruani.
              </div>
            )}
            <input
              id="f-file"
              type="file"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setRemoveFile(false);
              }}
              className="w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
            />
          </div>
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Anulo
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </button>
        </div>
      </form>
    </div>
  );
}
